/**
 * @file AdminReportsPage.tsx
 * @brief Super Admin → Reports hub + Phase A P0 report views.
 *
 * @details
 * `/admin/reports` lists the catalog; `/admin/reports/:reportId` opens a
 * table/export view. Distinct from Analytics (charts). Related:
 * docs/product/reporting.md, features/analytics/AnalyticsPage.tsx.
 *
 * @author DES
 * @date 27-07-2026
 * @updated 29-07-2026 — GridBlock catalog layout
 */

import { Link, useParams } from "react-router-dom";
import { FileChartColumn } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { cn } from "@/shared/theme/cn";
import { BillingAnomaliesReport } from "./components/BillingAnomaliesReport";
import { CompanyHealthReport } from "./components/CompanyHealthReport";
import { CostMarginReport } from "./components/CostMarginReport";
import { EnterprisePipelineReportView } from "./components/EnterprisePipelineReport";
import { GrowthReport } from "./components/GrowthReport";
import { LanguageDemandReport } from "./components/LanguageDemandReport";
import { QualityOpsReportView } from "./components/QualityOpsReport";
import { RevenueByProductReport } from "./components/RevenueByProductReport";
import {
  ADMIN_REPORT_CATALOG,
  getAdminReport,
} from "./reportCatalog";

function ReportsCatalog() {
  const available = ADMIN_REPORT_CATALOG.filter((r) => r.available);
  const upcoming = ADMIN_REPORT_CATALOG.filter((r) => !r.available);

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Reports</h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Date-ranged, exportable tables. Use Analytics for chart exploration.
          </p>
        </GridBand>

        <GridBand className="py-2.5">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Available
          </h3>
        </GridBand>

        <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
          {available.map((report) => {
            const Icon = report.icon;
            return (
              <GridCell key={report.id} padded={false}>
                <Link
                  to={`/admin/reports/${report.id}`}
                  className={cn(
                    "group flex h-full flex-col p-4 transition-colors md:p-5",
                    "hover:bg-muted/30",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex h-9 items-center text-brand">
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge variant="outline">{report.priority}</Badge>
                  </div>
                  <h4 className="mt-3 font-medium group-hover:text-brand">
                    {report.title}
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {report.description}
                  </p>
                </Link>
              </GridCell>
            );
          })}
        </GridCells>

        {upcoming.length > 0 ? (
          <>
            <GridBand className="py-2.5">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Coming soon
              </h3>
            </GridBand>
            <GridCells className="sm:grid-cols-2 xl:grid-cols-3">
              {upcoming.map((report) => {
                const Icon = report.icon;
                return (
                  <GridCell
                    key={report.id}
                    className="opacity-70"
                    padded={false}
                  >
                    <div className="flex h-full flex-col p-4 md:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-9 items-center text-muted-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <Badge variant="muted">{report.priority}</Badge>
                      </div>
                      <h4 className="mt-3 text-base font-medium">
                        {report.title}
                      </h4>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {report.description}
                      </p>
                    </div>
                  </GridCell>
                );
              })}
            </GridCells>
          </>
        ) : null}
      </GridBlock>
    </div>
  );
}

export function AdminReportsPage() {
  const { reportId } = useParams<{ reportId?: string }>();

  if (!reportId) {
    return <ReportsCatalog />;
  }

  const meta = getAdminReport(reportId);
  if (!meta || !meta.available) {
    return (
      <div className={gridPageBleedClass("pb-6")}>
        <GridBlock>
          <GridBand>
            <Link
              to="/admin/reports"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← All reports
            </Link>
          </GridBand>
          <GridCell>
            <div className="flex items-center gap-2 text-base font-semibold">
              <FileChartColumn className="h-4 w-4" />
              Report not available
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              This report id is unknown or not shipped yet.
            </p>
          </GridCell>
        </GridBlock>
      </div>
    );
  }

  switch (meta.id) {
    case "sa-revenue-product":
      return <RevenueByProductReport />;
    case "sa-company-health":
      return <CompanyHealthReport />;
    case "sa-cost-margin":
      return <CostMarginReport />;
    case "sa-language-demand":
      return <LanguageDemandReport />;
    case "sa-billing-anomalies":
      return <BillingAnomaliesReport />;
    case "sa-growth":
      return <GrowthReport />;
    case "sa-enterprise-pipeline":
      return <EnterprisePipelineReportView />;
    case "sa-quality-ops":
      return <QualityOpsReportView />;
    default:
      return <ReportsCatalog />;
  }
}
